import { Td } from 'components/Td/Td'
import { CircleButton } from 'pages/Shifts/Shifts.styled'
import { darkTheme, Flex, lightTheme, Table as BaseTable, TableBody as BaseTableBody, TableWrapper, Th, Tr } from 'styling/GlobalStyles'
import { EmployeeAvatar } from 'components/ui/EmployeeAvatar/EmployeeAvatar'
import { AttendanceMonthlyCard, AttendanceOption, AttendanceOptionsWrapper } from './Attendance.styled'
import { addZero, areDatesSameDay, dateToMinutesUTC } from 'utils/helpers'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BodyText, OtherText } from 'styling/Typography'
import { IAttendanceWorkScheduleInfo, IGetAllAttendanceSingleResponse } from 'types/api/attendanceApi.interface'
import { attendanceTypesOptions, attendanceTypesOptionsWithTime } from './data'
import { AppDispatch, RootState } from 'store'
import { clearAttendances, createAttendance, createMultipleAttendance, deleteAttendance, getAttendances } from 'features/attendance/attendanceSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import styled from 'styled-components'
import { FloatingDropdown } from 'components/FloatingDropdown/FloatingDropdown'
import { tokens } from 'styling/tokens'

const getMonthlyGridTemplate = (daysCount: number) => `2.75rem minmax(13rem, 15rem) repeat(${daysCount}, minmax(1.75rem, 1fr))`

const getMonthlyMinWidth = (daysCount: number) => `calc(2.75rem + 13rem + ${daysCount} * 1.75rem)`

const MonthlyHeaderRow = styled(Th) <{ daysCount: number }>`
    display: grid;
    grid-template-columns: ${({ daysCount }) => getMonthlyGridTemplate(daysCount)};
    justify-content: unset;
    gap: 0;
    width: 100%;
    min-width: ${({ daysCount }) => getMonthlyMinWidth(daysCount)};
`

const MonthlyBodyRow = styled(Tr) <{ daysCount: number }>`
    display: grid;
    grid-template-columns: ${({ daysCount }) => getMonthlyGridTemplate(daysCount)};
    justify-content: unset;
    gap: 0;
    width: 100%;
    min-width: ${({ daysCount }) => getMonthlyMinWidth(daysCount)};
`

const Table = styled(BaseTable)`
    overflow: visible;
    @media screen and (max-width: 1100px) {
        overflow: visible;
        margin: 0;
        padding: 0;
    }
`

const TableBody = styled(BaseTableBody)`
    overflow: visible;
    height: auto;
    padding: 0;
`

export const MonthlyDisplay = ({ params, attendance, setModal }: { params: any, attendance: IGetAllAttendanceSingleResponse[], setModal: any }) => {
    const [checked, setChecked] = useState([])
    const { darkMode } = useSelector((store: RootState) => store.globals)
    const [openDropdown, setOpenDropdown] = useState<{ userId: number, day: number, anchor: HTMLElement } | null>(null)
    const dispatch = useDispatch<AppDispatch>()
    useQueryPagination({ fetchFunctions: [getAttendances], cleanUpFunctions: [clearAttendances], params, otherFunctions: [() => setChecked([])] })

    const monthDays = useMemo(() => {
        const arr: { dayNumber: string, dayOfWeek: number }[] = []
        const date = new Date(params.dateFrom)
        const year = date.getFullYear()
        const month = date.getMonth()
        const day = new Date(year, month, 1)
        const daysInMonth = new Date(year, month + 1, 0).getDate()

        for (let i = 1; i <= daysInMonth; i++) {
            arr.push({
                dayNumber: `${addZero(day.getDate())}`,
                dayOfWeek: day.getDay(),
            })
            day.setDate(day.getDate() + 1)
        }

        return arr
    }, [params.dateFrom])

    const renderAttendanceInputs = (dates: IAttendanceWorkScheduleInfo[], userId: number, userName: string) => {

        const arr = []
        const date = new Date(params.dateFrom)
        const year = date.getFullYear()
        const month = date.getMonth()
        const day = new Date(year, month, 1)
        const daysInMonth = new Date(year, month + 1, 0).getDate()

        for (let i = 1; i <= daysInMonth; i++) {
            const attendanceForTheDay: IAttendanceWorkScheduleInfo = dates.find((dt) => {
                return areDatesSameDay(dt.date, day)
            })

            const { color, label } = attendanceTypesOptions.find(o => o.value == attendanceForTheDay?.workScheduleAttendanceInfo?.type) || { color: tokens.border.strong, label: 'Not Set' }
            const hasAttendance = !!attendanceForTheDay?.workScheduleAttendanceInfo?.type

            const handleDropdown = (e: React.MouseEvent<HTMLDivElement>) => {
                const anchor = e.currentTarget
                if (openDropdown && openDropdown.userId === userId && openDropdown.day === i) {
                    setOpenDropdown(null)
                } else {
                    setOpenDropdown({ userId, day: i, anchor })
                }
            }

            const handleAttendance = async (a: { label: string, value: number, color: string }, id?: number) => {

                if (id) {
                    await dispatch(deleteAttendance({ id, userId }))
                    return
                }

                const currentDate = new Date(Date.UTC(year, month, i, 0, 0, 0, 0))
                const obj: any = {
                    id: attendanceForTheDay?.workScheduleAttendanceInfo?.id || null,
                    userId,
                    attendanceType: a,
                    checkIn: hasAttendance ? dateToMinutesUTC(attendanceForTheDay?.workScheduleAttendanceInfo.startDate) : (attendanceForTheDay?.workSchedule?.shift?.shiftStart || null),
                    checkOut: hasAttendance ? dateToMinutesUTC(attendanceForTheDay.workScheduleAttendanceInfo.endDate) : (attendanceForTheDay?.workSchedule?.shift?.minutes ? attendanceForTheDay?.workSchedule?.shift?.minutes + attendanceForTheDay?.workSchedule?.shift?.shiftStart : null),
                    offset: attendanceForTheDay?.workScheduleAttendanceInfo?.offset || null,
                    offsetType: attendanceForTheDay?.workScheduleAttendanceInfo?.offsetType || null,
                    shiftId: attendanceForTheDay?.workSchedule?.id || null,
                    date: currentDate,
                    userName: userName,
                    shiftName: attendanceForTheDay?.workSchedule?.shift?.name || null,
                }

                if (a.label.includes('(time input)') || (!attendanceForTheDay?.workSchedule?.id && [1, 4, 8, 11].includes(a.value))) {
                    if (a.value === 11) {
                        obj.attendanceType = attendanceTypesOptions.find(at => at.value == 11)
                    } else {
                        obj.attendanceType = attendanceTypesOptions.find(at => at.value == Number(a.value.toString().split('')[0]))
                    }
                    setModal({
                        open: true, value: { ...obj, userIds: checked }
                    })
                } else {
                    if (checked.length) {
                        delete obj.shiftName
                        delete obj.userName
                        if ([1, 4, 8, 111, 444, 888, 11].includes(a.value)) {
                            obj.checked = checked
                            if (a.value === 11) {
                                obj.attendanceType = attendanceTypesOptions.find(at => at.value == 11)
                            }
                            if (a.label.includes('(time input)')) {
                                obj.attendanceType = attendanceTypesOptions.find(at => at.value == Number(a.value.toString().split('')[0]))
                            }
                            obj.workScheduleIds = attendance.filter(at => checked.includes(at.id)).map(at => at.workScheduleInfos.find(i => areDatesSameDay(i.date, currentDate))?.workSchedule?.id).filter(s => s),
                                setModal({
                                    open: true, value: obj
                                })
                        } else {
                            await dispatch(createMultipleAttendance({
                                userIds: checked,
                                workScheduleIds: attendance.filter(at => checked.includes(at.id)).map(at => at.workScheduleInfos.find(i => areDatesSameDay(i.date, currentDate))?.workSchedule?.id).filter(s => s),
                                startDate: currentDate.toISOString(),
                                endDate: null,
                                type: a.value
                            }))
                        }
                    } else {
                        await dispatch(createAttendance(obj))
                    }
                }
                setOpenDropdown(null)
            }

            arr.push(
                <Td key={userId + '-' + i} noTooltip>
                    <Flex justcenter width='100%'>
                        <AttendanceMonthlyCard color={((new Date(attendanceForTheDay?.date) < new Date()) && attendanceForTheDay?.workSchedule && !attendanceForTheDay.workScheduleAttendanceInfo && !attendanceForTheDay?.workSchedule?.shift.absence) ? (darkMode ? lightTheme['presentColor'] : darkTheme['presentColor']) : darkMode ? lightTheme[color] : darkTheme[color]} onClick={handleDropdown} >
                            <OtherText color={tokens.text.primary} weight='bold' >{hasAttendance ? label[0] : '/'}</OtherText>
                            <FloatingDropdown
                                open={!!(openDropdown && openDropdown.userId === userId && openDropdown.day === i)}
                                onOpenChange={(o) => { if (!o) setOpenDropdown(null) }}
                                reference={openDropdown && openDropdown.userId === userId && openDropdown.day === i ? openDropdown.anchor : null}
                            >
                                <AttendanceOptionsWrapper>
                                    {attendanceForTheDay?.workScheduleAttendanceInfo && <AttendanceOption onClick={() => handleAttendance(null, attendanceForTheDay?.workScheduleAttendanceInfo.id)} >
                                        <OtherText color={tokens.status.danger.fg} >Remove Attendance</OtherText>
                                    </AttendanceOption>}
                                    {attendanceTypesOptionsWithTime.filter(a => attendanceForTheDay?.workSchedule?.shift?.absence ? ![1, 4, 8, 11].includes(a.value) : true).map(a => <AttendanceOption onClick={() => handleAttendance(a)} key={a.value}><OtherText>{a.label}</OtherText></AttendanceOption>)}
                                </AttendanceOptionsWrapper>
                            </FloatingDropdown>
                        </AttendanceMonthlyCard>
                    </Flex>
                </Td>,
            )
            day.setDate(day.getDate() + 1)
        }

        return arr
    }
    const toggleAll = () => {
        if (checked.length < attendance.length) {
            for (const a of attendance) {
                const id = a.id

                if (!checked.includes(id)) {
                    setChecked((prev) => [...prev, id])
                }
            }
        } else {
            setChecked([])
        }
    }


    const toggle = (id: number) => {
        if (checked.includes(id)) {
            setChecked((prev) => {
                return prev.filter((item) => item !== id)
            })
        } else {
            setChecked((prev) => (!prev ? [id] : [...prev, id]))
        }
    }

    return <TableWrapper style={{ height: '100%' }} >
        <Table style={{ height: '100%' }} >
            <MonthlyHeaderRow daysCount={monthDays.length} >
                <Td noTooltip>
                    <Flex justcenter width='100%'>
                        <CircleButton
                            onClick={toggleAll}
                            checked={checked.length === attendance?.length}
                        />
                    </Flex>
                </Td>
                <Td noTooltip>
                    <BodyText weight='bold'>Employee</BodyText>
                </Td>
                {monthDays.map(({ dayNumber, dayOfWeek }, index) => (
                    <Td noTooltip key={`month-day-${index + 1}`}>
                        <Flex justcenter width='100%'>
                            <OtherText color={dayOfWeek == 0 || dayOfWeek == 6 ? tokens.accent.base : null}>{dayNumber}</OtherText>
                        </Flex>
                    </Td>
                ))}
            </MonthlyHeaderRow>
            <TableBody>
                {attendance &&
                    attendance.map((item) => {
                        const id = item.id
                        return (
                            <MonthlyBodyRow borderHover key={id} daysCount={monthDays.length}>
                                <Td noTooltip>
                                    <Flex justcenter width='100%'>
                                        <CircleButton
                                            orange
                                            checked={checked.includes(id)}
                                            onClick={() => toggle(id)}
                                        />
                                    </Flex>
                                </Td>
                                <Td noTooltip>
                                    <Flex center gap='1rem'>
                                        <EmployeeAvatar src={item.profileImageUrl} name={item.firstName + ' ' + item.lastName} />
                                        <BodyText>{item.firstName + ' ' + item.lastName}</BodyText>
                                    </Flex>
                                </Td>
                                {renderAttendanceInputs(item.workScheduleInfos, item.id, (item.firstName + ' ' + item.lastName))}
                            </MonthlyBodyRow>
                        )
                    })}
            </TableBody>

        </Table>
    </TableWrapper>
}
